import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

interface MatchRow {
  id: string;
  profileId: string;
  assignmentId: string;
  heuristicScore: string | null;
  aiScore: string | null;
  teamFitScore: string | null;
  matchedAt: string;
  profileName: string;
  profileEmail: string | null;
  assignmentTitle: string;
}

interface MatchDetail {
  id: string;
  profileId: string;
  assignmentId: string;
  heuristicScore: string | null;
  aiScore: string | null;
  teamFitScore: string | null;
  matchedAt: string;
  details: {
    matchId: string;
    reasons: string[];
    skillsMatch: string[];
    skillsGap: string[];
    recommendations: string[];
    pitch: string | null;
    gapsFilled: string[];
    redundancies: string[];
    teamImpact: string | null;
    breakdown: Record<string, unknown>;
  } | null;
}

export function useMatches(page = 1, limit = 50) {
  return useQuery({
    queryKey: ['matches', page, limit],
    queryFn: () => apiFetch<MatchRow[]>(`/matches?page=${page}&limit=${limit}`)
  });
}

interface MatchLookup {
  id: string;
  profileId: string;
  assignmentId: string;
  heuristicScore: string | null;
  aiScore: string | null;
  teamFitScore: string | null;
  matchedAt: string;
}

export function useMyMatchForAssignment(assignmentId: string | null) {
  return useQuery({
    queryKey: ['match-by-assignment', assignmentId],
    queryFn: () => apiFetch<MatchLookup | null>(`/matches/by-assignment/${assignmentId}`),
    enabled: !!assignmentId,
    staleTime: 30_000
  });
}

export function useMatchDetail(matchId: string | null) {
  return useQuery({
    queryKey: ['match', matchId],
    queryFn: () => apiFetch<MatchDetail>(`/matches/${matchId}`),
    enabled: !!matchId
  });
}

export function useRunMatching() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params?: { profileIds?: string[]; assignmentIds?: string[] }) =>
      apiFetch<{ runId: string; status: string }>('/matches/run', {
        method: 'POST',
        body: JSON.stringify(params ?? {})
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    }
  });
}

export function useMatchAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, assignmentId }: { profileId: string; assignmentId: string }) =>
      apiFetch<MatchRow & { details: MatchDetail['details']; aiError: string | null }>(
        '/matches/match-one',
        {
          method: 'POST',
          body: JSON.stringify({ profileId, assignmentId })
        }
      ),
    onSuccess: async (result, variables) => {
      await queryClient.refetchQueries({ queryKey: ['matches'] });
      await queryClient.refetchQueries({
        queryKey: ['match-by-assignment', variables.assignmentId]
      });
      if (result?.data?.id) {
        await queryClient.refetchQueries({ queryKey: ['match', result.data.id] });
      }
    }
  });
}

export function useGeneratePitch(matchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      opts: {
        language?: 'en' | 'sv';
        template?: string;
        instructions?: string;
      } = {}
    ) =>
      apiFetch<{ runId: string; status: string }>(`/matches/${matchId}/pitch`, {
        method: 'POST',
        body: JSON.stringify({
          language: opts.language ?? 'en',
          template: opts.template,
          instructions: opts.instructions
        })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['match', matchId] });
    }
  });
}

export function useDeleteMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => apiFetch<null>(`/matches/${matchId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    }
  });
}

export type { MatchRow, MatchDetail };
